import React, { useState, useEffect, useRef, useCallback  } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { DatesSetArg } from "@fullcalendar/core";
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { Hooks } from 'hooks/commonHooks';
import { API } from 'helpers/ApiHelpers';
import { Files } from 'helpers/FileHelpers';
import { Actions, Code, Control, Form, Name, Utility } from 'helpers/FormImports';
import { EventInput, EventContentArg } from '@fullcalendar/core';
import { Appointment } from 'models/AppointmentModel';
import { CustomSchedule } from 'models/CustomSchedule';
import { ScheduleCalendarStyle } from 'components/style/ScheduleCalendarStyle';
import Tippy from '@tippyjs/react';
import { APIUrl } from 'helpers/UrlHelpers'
import { CalendarHelper } from 'helpers/forms/CalendarHelpers';
import { co } from '@fullcalendar/core/internal-common';


const ScheduleCalendar = () => {
  const [formId, formName] = [Code.Form.Id.SCHEDULE, Code.Form.Name.SCHEDULE];
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [branchOptions, setBranchOptions] = Hooks.Form.OptionMore();
  const calendarRefs = useRef<Record<number, FullCalendar | null>>({});
  const [events, setEvents] = useState<Record<number, EventInput[]>>({});
  const [menuPosition, setMenuPosition] = Hooks.Form.Position(); 
  const [showMenu, setShowMenu] = useState<boolean>(false); 
  
  
  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  const doctorList = Utility.Convert.HashString(Utility.Storage.DoctorList());
  const iframeRef = Hooks.Form.Frame();
  
  useEffect(() => { 
    Files.Json.API(Code.File.LOCATION, Code.Option.Common.NOTHING, setBranchOptions, setError);
  }, []);


  const handleDatesSet = (branchId: number) => async (arg: DatesSetArg) => {
    const startDate = Utility.Format.Date(arg.startStr,Code.Format.DateTime.Date);
    const endDate = Utility.Format.Date(arg.endStr,Code.Format.DateTime.Date);
  
    try {
      const url =  APIUrl.ScheduleCalendar(companyId, branchId, doctorList, startDate, endDate);

      const response = await API.Data.return<CustomSchedule[]>(url, setError, setLoading);

      //console.log("Fetched events for branch--------->", url, response);

      if (!response || !Array.isArray(response)) {
        console.error('Invalid response data');
        return;
      }
      
      const flattenedResponse = response.flat();
      const formattedEvents: EventInput[] = CalendarHelper.mapScheduleToEvent(flattenedResponse, branchId);

      setEvents(prev => {
        const existingEvents = prev[branchId] || [];
        // Create a Set of existing event IDs for quick lookup
        const existingIds = new Set(existingEvents.map(e => e.id?.toString()));
        // Filter new events that aren't already in existingEvents
        const newUniqueEvents = formattedEvents.filter(e => !existingIds.has(e.id?.toString()));
        // Merge existing and new unique events
        const merged: EventInput[] = [...existingEvents, ...newUniqueEvents];
        return {
          ...prev,
          [branchId]: merged
        };
      });

    } catch (err) {
      console.error("Failed to load events:", err);
    }
  };

  useEffect(() => {
    //console.log("🟢 Updated events:", events);
  }, [events]);


  function renderEventContent(eventInfo: EventContentArg) {
    const handleEventClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const isCtrl = e.ctrlKey || e.metaKey || e.altKey;
      //console.log("---Schedule-Details--->",eventInfo);
      if(isCtrl)
      {
        Utility.Set.Reload(true);
        Form.Show.Popup(iframeRef, `${formName}/${eventInfo.event.id}`);
      }
      else
      {
        Utility.Set.Reload(false);
        Form.Show.Popup(iframeRef, "calendar/appointment", 
          Code.UrlPara.Doctor, eventInfo.event.extendedProps.doctorId,
          Code.UrlPara.Branch, eventInfo.event.extendedProps.branchId,
          Code.UrlPara.CalendarView, Code.Calendar.View.DayGrid,
          Code.UrlPara.StartDate, Utility.Format.Date(eventInfo.event.startStr)
        )
      }
      
    };


    return (
      <Tippy
        content={
          <div className='fc-event-info'>
            <b>{eventInfo.event.extendedProps.doctorName}</b>
            <div><p>Date</p><span>{Utility.Format.Date(eventInfo.event.start!,"MMM dd, yyyy (ddd)")}</span></div>
            <div><p>Start</p><span>{Utility.Format.Time(eventInfo.event.extendedProps.startTime)}</span></div>
            <div><p>End</p><span>{Utility.Format.Time(eventInfo.event.extendedProps.endTime)}</span></div>
            <div><p>Time</p><span>{Utility.Get.TotalHours(eventInfo.event.extendedProps.startTime,eventInfo.event.extendedProps.endTime)} Hr.</span></div>
            <div><p>Total</p><span>{eventInfo.event.extendedProps.total} Appointments</span></div>
          </div>
        }
        placement="top"
        delay={[300, 0]}
      >
        <div
          className="fc-schedule-count"
          style={{ backgroundColor: eventInfo.event.extendedProps.doctorColor }}
          onClick={handleEventClick}
        >
          <p>{eventInfo.event.extendedProps.total}</p>
          <span>{eventInfo.event.title}</span>
        </div>
      </Tippy>
    );
  }

  // Date click handler
  const handleDateClick = (arg: DateClickArg, branch:number) => {
    //console.log('Clicked date:', arg.dateStr);
    Utility.Set.Reload(true);
    Form.Show.Popup(iframeRef, formName, 
      Code.UrlPara.Branch, branch.toString(), 
      Code.UrlPara.StartDate, Utility.Format.Date(arg.date)
    )
  };

  return (
    <>
      <Control.Form.CalendarFrame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
        <ScheduleCalendarStyle />
        <Control.Resize className='pt2'>
          {[
              ...branchOptions.filter(b => b.value === locationId),
              ...branchOptions.filter(b => b.value !== locationId)
            ].map((branch) => (
            <Control.Column key={branch.value} size={[0,100,4]}>
              <div className='fc-schedule-title' style={{ borderTopColor: branch.data1, borderTopStyle: 'solid', borderTopWidth: '4px', color: branch.data1 }}>{branch.label}</div>
              <FullCalendar
                ref={(el) => (calendarRefs.current[branch.value] = el)}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                showNonCurrentDates={false}
                datesSet={handleDatesSet(branch.value)}
                headerToolbar={{ left: "title", center: "", right: `prev,next`}}
                height="auto"
                events={events[branch.value] || []}
                eventContent={renderEventContent}
                dateClick={(arg) => { handleDateClick(arg, branch.value); }} 
                />
            </Control.Column>
          ))}
        </Control.Resize>
      </Control.Form.CalendarFrame>
      <Control.FormPopup iframeRef={iframeRef} onRefresh={() => { if(Utility.Get.Reload()) { window.location.reload(); }}} />
      <Actions.Calendar />
    </>
  );

};

export default ScheduleCalendar;